import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Loader2, UserPlus, Trash2, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtEuro, parseNum } from "@/lib/dates";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null); // employee obj
  const [pwOpen, setPwOpen] = useState(null); // employee obj
  const [form, setForm] = useState({ name: "", username: "", password: "", gross_hourly_wage: "" });
  const [editForm, setEditForm] = useState({ name: "", gross_hourly_wage: "" });
  const [newPw, setNewPw] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await api.get("/users");
    setEmployees(res.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore nel caricamento"));
  }, []);

  const create = async () => {
    if (!form.name || !form.username || !form.password) return toast.error("Compila nome, username e password");
    setBusy(true);
    try {
      await api.post("/users", { ...form, gross_hourly_wage: parseNum(form.gross_hourly_wage) });
      toast.success("Dipendente creato");
      setCreateOpen(false);
      setForm({ name: "", username: "", password: "", gross_hourly_wage: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (e) => {
    setEditForm({ name: e.name, gross_hourly_wage: e.gross_hourly_wage ? String(e.gross_hourly_wage).replace(".", ",") : "" });
    setEditOpen(e);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast.error("Il nome è obbligatorio");
    setBusy(true);
    try {
      await api.put(`/users/${editOpen.id}`, {
        name: editForm.name,
        gross_hourly_wage: parseNum(editForm.gross_hourly_wage),
      });
      toast.success("Dipendente aggiornato");
      setEditOpen(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changePw = async () => {
    if (!newPw) return toast.error("Inserisci la nuova password");
    setBusy(true);
    try {
      await api.put(`/users/${pwOpen.id}/password`, { password: newPw });
      toast.success("Password aggiornata");
      setPwOpen(null);
      setNewPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeEmp = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Dipendente eliminato");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const inputCls = "w-full h-14 rounded-xl border border-slate-200 px-4 text-lg outline-none focus:border-primary";
  const iconBtn = "h-11 w-11 rounded-xl border flex items-center justify-center active:scale-95";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-bold text-slate-900">Dipendenti</h1>
        <button
          data-testid="new-employee-button"
          onClick={() => setCreateOpen(true)}
          className="h-12 px-4 rounded-xl bg-primary text-primary-foreground font-bold flex items-center gap-1.5 active:scale-95"
        >
          <Plus className="h-5 w-5" /> Nuovo
        </button>
      </div>

      <div className="space-y-2">
        {employees.length === 0 && <p className="text-slate-400 text-sm">Nessun dipendente. Crea il primo account.</p>}
        {employees.map((e) => (
          <div key={e.id} data-testid="employee-card" className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 text-lg truncate">{e.name}</p>
              <p className="text-sm text-slate-500 truncate">
                @{e.username}
                {e.gross_hourly_wage ? (
                  <span data-testid={`emp-wage-${e.username}`}> · {fmtEuro(e.gross_hourly_wage)}/h lordi</span>
                ) : (
                  <span className="text-amber-500"> · retribuzione non impostata</span>
                )}
              </p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button data-testid={`edit-emp-${e.username}`} onClick={() => openEdit(e)} className={`${iconBtn} border-slate-200 text-slate-600`} aria-label="Modifica">
                <Pencil className="h-4 w-4" />
              </button>
              <button data-testid={`change-pw-${e.username}`} onClick={() => setPwOpen(e)} className={`${iconBtn} border-slate-200 text-slate-600`} aria-label="Password">
                <KeyRound className="h-4 w-4" />
              </button>
              <button data-testid={`delete-emp-${e.username}`} onClick={() => setDeleteTarget(e)} className={`${iconBtn} border-red-200 text-destructive`} aria-label="Elimina">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><UserPlus className="h-5 w-5" /> Nuovo dipendente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <input data-testid="emp-name" className={inputCls} placeholder="Nome e cognome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="emp-username" className={inputCls} autoCapitalize="none" placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            <input data-testid="emp-password" className={inputCls} placeholder="Password iniziale" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1 block">Retribuzione oraria lorda (€/h)</label>
              <input data-testid="emp-wage" inputMode="decimal" className={inputCls} placeholder="es. 12,50" value={form.gross_hourly_wage} onChange={(e) => setForm({ ...form, gross_hourly_wage: e.target.value })} />
              <p className="text-xs text-slate-400 mt-1">Usata solo per il calcolo dei costi in Redditività (non visibile ai dipendenti).</p>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-employee-button" onClick={create} disabled={busy} className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Crea dipendente"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editOpen} onOpenChange={(o) => !o && setEditOpen(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">Modifica dipendente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1 block">Nome e cognome</label>
              <input data-testid="edit-emp-name" className={inputCls} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1 block">Retribuzione oraria lorda (€/h)</label>
              <input data-testid="edit-emp-wage" inputMode="decimal" className={inputCls} placeholder="es. 12,50" value={editForm.gross_hourly_wage} onChange={(e) => setEditForm({ ...editForm, gross_hourly_wage: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-edit-employee-button" onClick={saveEdit} disabled={busy} className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva modifiche"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && setPwOpen(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">Cambia password{pwOpen ? ` · ${pwOpen.name}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <input data-testid="new-password-input" className={inputCls} placeholder="Nuova password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          </div>
          <DialogFooter>
            <button data-testid="save-password-button" onClick={changePw} disabled={busy} className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Aggiorna password"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati l'account del dipendente e tutte le sue ore e richieste ferie. L'operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-emp" onClick={removeEmp} className="bg-destructive hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
